import _ from "lodash";
import moment from 'moment';
import Vue from 'vue';
import { PTSI_ADMIN_ROLES, ROLES, ALL_PTSI_ROLES, ALL_INDONESIA_ROLES } from "./constants/roles";
import { PERMISSIONS } from "./constants/permissions";

const SOCIETY_ADMIN_BASE_ROLES = new Set(['naccu_society_admin', 'ptsi_society_admin'])

export function getBaseRoleId(roleId) {
  return String(roleId || '').toLowerCase().replace(/_\d+$/, '')
}

export function isSocietyAdminRole(roleId) {
  return SOCIETY_ADMIN_BASE_ROLES.has(getBaseRoleId(roleId))
}

export function convertSizeIntoBaseUnit(farmSize) {
  const currentAreaUnit = JSON.parse(localStorage.getItem("gs"))?.areaUnit;
  let convertedFarmSize = farmSize;

  if (currentAreaUnit) {
    const factor = currentAreaUnit.factor;
    if (currentAreaUnit.name !== "Acre" && factor > 0) {
      convertedFarmSize = farmSize / factor;
    }
  }

  const currentAreaUnitName = currentAreaUnit ? currentAreaUnit.name : "Acre";

  return convertedFarmSize ? parseFloat(convertedFarmSize).toFixed(3) + " " + currentAreaUnitName : 0 + " " + currentAreaUnitName;
}

export function convertSizeIntoBaseUnitNumberOnly(farmSize) {
  const currentAreaUnit = JSON.parse(localStorage.getItem("gs"))?.areaUnit;
  let convertedFarmSize = farmSize;

  if (currentAreaUnit) {
    const factor = currentAreaUnit.factor;
    if (currentAreaUnit.name !== "Acre" && factor > 0) {
      convertedFarmSize = farmSize / factor;
    }
  }

  // const currentAreaUnitName = currentAreaUnit ? currentAreaUnit.name : "Acre";

  return parseFloat(convertedFarmSize).toFixed(3);
}

export function convertWeightIntoBaseUnit(weight) {
  const currentWeightUnit = JSON.parse(localStorage.getItem("gs"))?.weightUnit;
  let convertedWeight = weight;

  if (currentWeightUnit) {
    const factor = currentWeightUnit.factor;
    if (currentWeightUnit.name !== "Kilogram" && factor > 0) {
      convertedWeight = weight / factor;
    }
  }

  const currentWeightUnitName = currentWeightUnit ? currentWeightUnit.abbvr : "KG";

  return convertedWeight ? parseFloat(convertedWeight).toFixed(3) + " " + currentWeightUnitName : 0 + " " + currentWeightUnitName;
}

export function convertWeightIntoBaseUnitNumberOnly(weight) {
  const currentWeightUnit = JSON.parse(localStorage.getItem("gs"))?.weightUnit;
  let convertedWeight = weight;

  if (currentWeightUnit) {
    const factor = currentWeightUnit.factor;
    if (currentWeightUnit.name !== "Kilogram" && factor > 0) {
      convertedWeight = weight / factor;
    }
  }

  return parseFloat(convertedWeight).toFixed(3);
}

export function getRandomColor() {
  const red = _.random(_.random(100, 200, 0), 255, 0);
  const green = _.random(_.random(100, 200, 0), 255, 0);
  const blue = _.random(_.random(100, 200, 0), 255, 0);
  const color = "#" + red.toString(16) + green.toString(16) + blue.toString(16);
  return color;
}

export function getRandomColors() {
  const randomColor = Math.floor(Math.random() * 16777215).toString(16);
  return `#${randomColor.padStart(6, "0")}`; // Ensure the color has 6 digits
}
export function hexToRgb(hex) {
  const bigint = parseInt(hex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b];
}
export function getColorByBgColor(hex) {
  const [r, g, b] = hexToRgb(hex);
  return ((r * 0.299) + (g * 0.587) + (b * 0.114)) > 186 ? '#000000' : '#ffffff';
}
export function colorDistance(color1, color2) {
  const [r1, g1, b1] = hexToRgb(color1);
  const [r2, g2, b2] = hexToRgb(color2);
  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}
export function isColorTooClose(newColor, colorMap) {
  const threshold = 100; // Adjust this threshold as needed
  for (let key in colorMap) {
    if (colorDistance(newColor, colorMap[key]) < threshold) {
      return true;
    }
  }
  return false;
}
export function getColorHash(colorMap) {
  let fillColor;
  do {
    fillColor = getRandomColors();
  } while (isColorTooClose(fillColor, colorMap));
  return fillColor;
}

  /**
   * Map a deforestation status string to its CSS class and display text.
   *
   * @param {string}   status  - The raw status value from the API.
   * @param {Function} [$t]    - Optional Vue i18n translation function (this.$t).
   *                            When provided, display text is translated.
   *                            When omitted, English fallback strings are returned.
   */
  export function getEUDRDeforestationStatus(status, $t) {
    const t = (key, fallback) => ($t ? $t(key) : fallback);
    switch (status) {
        case 'Zero/Negligible Deforestation Probability':
        case 'Zero/Negligible Probability':
            return { class: 'green-button', text: t('deforestation.zeroOrNegligibleProbability', 'Zero/Negligible Deforestation Probability') };
        case 'Compliant':
              return { class: 'green-button', text: t('deforestation.compliant', 'Compliant') };
        case 'Non-compliant':
              return { class: 'red-button', text: t('deforestation.nonCompliant', 'Non-Compliant') };
        case 'Zero/Negligible Risk':
            return { class: 'green-button', text: t('deforestation.zeroOrNegligibleRisk', 'Zero/Negligible Risk') };
        case 'Very High Deforestation Probability':
        case 'Very High Probability':
            return { class: 'very-high-button', text: t('deforestation.veryHighProbability', 'Very High Deforestation Probability') };
        case 'Very Low Deforestation Probability':
        case 'Very Low Probability':
            return { class: 'very-low-button', text: t('deforestation.veryLowProbability', 'Very Low Deforestation Probability') };
        case 'High Deforestation Probability':
        case 'High Probability':
            return { class: 'red-button', text: t('deforestation.highProbability', 'High Deforestation Probability') };
        case 'Low Deforestation Probability':
        case 'Low Probability':
            return { class: 'orange-button', text: t('deforestation.lowProbability', 'Low Deforestation Probability') };
        case 'Medium Deforestation Probability':
        case 'Medium Probability':
            return { class: 'medium-button', text: t('deforestation.mediumProbability', 'Medium Deforestation Probability') };
        case 'Manually Deforestation Mitigated':
        case 'Manually Mitigated':
            return { class: 'manually-button', text: t('dueDiligence.manualMitigated', 'Manually Mitigated') };
        case 'Risk Detected':
            return { class: 'red-button', text: t('deforestation.riskDetected', 'Risk Detected') };
        // ── Error / processing statuses ──────────────────────────────────
        case 'unknown_error':
            return { class: 'deep-orange-darken-1-button', text: t('dueDiligence.unknown_error', 'Unknown Error') };
        case 'geo_location_error':
            return { class: 'deep-orange-darken-1-button', text: t('dueDiligence.geo_location_error', 'Geo Location Error') };
        case 'region_not_available':
            return { class: 'deep-orange-darken-1-button', text: t('dueDiligence.region_not_available', 'Region Not Available') };
        case 'area_too_large':
            return { class: 'deep-orange-darken-1-button', text: t('dueDiligence.area_too_large', 'Area Too Large') };
        case 'area_too_small':
            return { class: 'deep-orange-darken-1-button', text: t('dueDiligence.area_too_small', 'Area Too Small') };
        case 'invalid_request_data':
            return { class: 'deep-orange-darken-1-button', text: t('dueDiligence.invalid_request_data', 'Invalid Request Data') };
        case 'no_processed_images_found':
            return { class: 'deep-orange-darken-1-button', text: t('dueDiligence.no_processed_images_found', 'No Processed Images Found') };
        case 'invalid_polygon':
            return { class: 'deep-orange-darken-1-button', text: t('dueDiligence.invalid_polygon', 'Invalid Polygon') };
        // ── Tooltip (_tt) variants ────────────────────────────────────────
        case 'unknown_error_tt':
            return { class: 'unknown_error', text: t('dueDiligence.unknown_error_tt', 'An unknown error occurred while processing this farm.') };
        case 'geo_location_error_tt':
            return { class: 'geo_location_error', text: t('dueDiligence.geo_location_error_tt', 'The geo location coordinates are invalid or missing.') };
        case 'region_not_available_tt':
            return { class: 'region_not_available', text: t('dueDiligence.region_not_available_tt', 'This region is not currently supported for deforestation analysis.') };
        case 'area_too_large_tt':
            return { class: 'area_too_large', text: t('dueDiligence.area_too_large_tt', 'The farm area exceeds the maximum size allowed for analysis.') };
        case 'area_too_small_tt':
            return { class: 'area_too_small', text: t('dueDiligence.area_too_small_tt', 'The farm area is below the minimum size required for analysis.') };
        case 'invalid_request_data_tt':
            return { class: 'invalid_request_data', text: t('dueDiligence.invalid_request_data_tt', 'The request data contains invalid or missing fields.') };
        case 'no_processed_images_found_tt':
            return { class: 'no_processed_images_found', text: t('dueDiligence.no_processed_images_found_tt', 'No processed satellite images were found for this area.') };
        case 'invalid_polygon_tt':
            return { class: 'invalid_polygon', text: t('dueDiligence.invalid_polygon_tt', 'The farm boundary polygon is invalid or self-intersecting.') };
        default:
            return { class: '', text: 'N/A' };
        }
        }

const ZERO_NEGLIGIBLE_EUDR_STATUSES = new Set([
  'zero/negligible deforestation probability',
  'zero/negligible probability',
  'zero/negligible risk',
])

const MANUALLY_MITIGATED_EUDR_STATUSES = new Set([
  'manually mitigated',
  'manually deforestation mitigated',
])

export function isZeroOrNegligibleEudrStatus(status) {
  if (!status) return false
  const normalized = String(status).trim().toLowerCase()
  if (ZERO_NEGLIGIBLE_EUDR_STATUSES.has(normalized)) return true
  return normalized.includes('zero') && normalized.includes('negligible')
}

/** PTSI EUDR step: allow proceed when status is zero/negligible, manually mitigated, or N/A. */
export function isAllowedPtsiEudrProceedStatus(status) {
  if (status == null || String(status).trim() === '') return true
  const normalized = String(status).trim().toLowerCase()
  if (normalized === 'n/a') return true
  if (ZERO_NEGLIGIBLE_EUDR_STATUSES.has(normalized)) return true
  if (normalized.includes('zero') && normalized.includes('negligible')) return true
  if (MANUALLY_MITIGATED_EUDR_STATUSES.has(normalized)) return true
  if (normalized.includes('manually mitigated')) return true
  return false
}

export function normalizeReportRiskStatusKey(riskStatus) {
  const normalized = String(riskStatus || '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === 'high_risk' || normalized === 'high risk') return 'high_risk';
  if (normalized === 'medium_risk' || normalized === 'medium risk') return 'medium_risk';
  if (normalized === 'compliant') return 'compliant';
  if (normalized === 'manually_mitigated' || normalized === 'manually mitigated') return 'manually_mitigated';
  if (normalized === 'zero_negligible' || normalized === 'zero/negligible' || normalized === 'zero negligible') return 'zero_negligible';
  if (normalized.includes('medium deforestation') || normalized === 'medium probability') return 'medium_risk';
  if (normalized.includes('very high') || normalized.includes('high deforestation') || normalized === 'high probability') {
    return 'high_risk';
  }
  return null;
}

export function getReportRiskStatusLabel(riskStatus, $t) {
  const t = (key, fallback) => ($t ? $t(key) : fallback);
  const key = normalizeReportRiskStatusKey(riskStatus);
  if (key === 'high_risk') return t('deforestation.riskStatusHigh', 'High Risk');
  if (key === 'medium_risk') return t('deforestation.riskStatusMedium', 'Medium Risk');
  if (key === 'compliant') return t('deforestation.riskStatusZeroNegligible', 'Zero/Negligible');
  if (key === 'manually_mitigated') return t('dueDiligence.manualMitigated', 'Manually Mitigated');
  if (key === 'zero_negligible') return t('deforestation.riskStatusZeroNegligible', 'Zero/Negligible');
  return '-';
}

export function getReportRiskStatusMeta(riskStatus, $t) {
  const t = (key, fallback) => ($t ? $t(key) : fallback);
  const key = normalizeReportRiskStatusKey(riskStatus);
  if (key === 'high_risk') {
    return { label: t('deforestation.riskStatusHigh', 'High Risk'), badgeClass: 'red-button' };
  }
  if (key === 'medium_risk') {
    return { label: t('deforestation.riskStatusMedium', 'Medium Risk'), badgeClass: 'orange-button' };
  }
  if (key === 'compliant') {
    return { label: t('deforestation.riskStatusZeroNegligible', 'Zero/Negligible'), badgeClass: 'green-button' };
  }
  if (key === 'manually_mitigated') {
    return { label: t('dueDiligence.manualMitigated', 'Manually Mitigated'), badgeClass: 'manually-button' };
  }
  if (key === 'zero_negligible') {
    return { label: t('deforestation.riskStatusZeroNegligible', 'Zero/Negligible'), badgeClass: 'green-button' };
  }
  return null;
}


export const getEUDRStatusOptions = () => [
    { text: "All Status", val: "" },
    { text: "Very High Deforestation Probability", val: "Very High Deforestation Probability" },
    { text: "High Deforestation Probability", val: "High Deforestation Probability" },
    { text: "Medium Deforestation Probability", val: "Medium Deforestation Probability" },
    { text: "Low Deforestation Probability", val: "Low Deforestation Probability" },
    { text: "Very Low Deforestation Probability", val: "Very Low Deforestation Probability" },
    { text: "Zero/Negligible Deforestation Probability", val: "Zero/Negligible Deforestation Probability" },
    { text: "Zero/Negligible Risk", val: "Zero/Negligible Risk" },
    { text: "Risk Detected", val: "Risk Detected" },
    { text: "Manually Deforestation Mitigated", val: "Manually Deforestation Mitigated" },
];

export const getRiskAssessmentStatusOptions = () => [
    { text: "All Status", val: "" },
    { text: "Approved", val: "approved" },
    { text: "Mitigation Required", val: "mitigation_required" },
    { text: "Manually Mitigated", val: "manually_mitigated" },
    { text: "Not Applicable", val: "not_applicable" },
    { text: "Assessment Expired", val: "expired" },
    { text: "Rejected", val: "rejected" },
];

/**
 * Display mapping for a farm's risk assessment status.
 *
 * `manually_mitigated` and `not_applicable` are set by automatic classification:
 * the first when every risky answer has a completed, still-valid mitigation, the
 * second when the assessment has no risk-classified questions at all. Both are
 * compliant. Without them here they would fall through to 'N/A', which callers
 * read as "no assessment yet" and would hide the badge.
 */
export function getRiskAssessmentStatus(status) {
    switch (status) {
        case 'approved':
            return { class: 'green-button', text: 'Approved' };
        case 'mitigation_required':
            return { class: 'orange-button', text: 'Mitigation Required' };
        case 'manually_mitigated':
            return { class: 'manually-button', text: 'Manually Mitigated' };
        case 'not_applicable':
            return { class: 'gray-button', text: 'Not Applicable' };
        case 'expired':
            // Validity window lapsed: the questionnaire has to be answered
            // again, which is why this is not 'Mitigation Required'.
            return { class: 'red-button', text: 'Assessment Expired' };
        case 'rejected':
            return { class: 'red-button', text: 'Rejected' };
        default:
            return { class: '', text: 'N/A' };
        }
  }

export async function wait(milliseconds = 1000) {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
}
export async function withRetry(operation, config = { retryTimes: 5, retryDelay: 3000 }) {
  const retryTimes = config.retryTimes;
  const retryDelay = config.retryDelay;
  if (typeof operation !== 'function') return;
  return new Promise((resolve, reject) => {
    operation()
      .then(resolve)
      .catch((error) => {
        if (retryTimes > 0) {
          return wait(retryDelay)
            .then(withRetry.bind(null, operation, { retryTimes: retryTimes - 1, retryDelay }))
            .then(resolve)
            .catch(reject);
        }
        return reject(error);
      });
  });
}

export function currentRoles() {
  const user = JSON.parse(localStorage.getItem('user'));
  if(!user) return [];
  return user.user_role_assoc.map(role => role.id);
}

export function isDeforestationExpired(date, eudrSetting) {
  if(!date || !eudrSetting || !eudrSetting.dynamicExpiryTime || !eudrSetting.dynamicExpiryTimePeriod) return true;
  const expiryDate = moment(date).add(eudrSetting.dynamicExpiryTime, eudrSetting.dynamicExpiryTimePeriod);
  return expiryDate.isAfter(moment.now());
}

// const INDONESIA_CLIENT_ROLES = [
//   ...INDONESIA_ROLES,
//   'dds_exporter',
//   'dds_ptsi',
// ];

const INDONESIA_PTSI_CLIENT_ROLES = [
  ROLES.indonesia_ptsi.value,
  ROLES.indonesia_ptsi_worker.value,
  'dds_ptsi',
];

const KENYA_CLIENT_ROLES = [
  ROLES.kenya_ptsi.value,
  ROLES.kenya_ptsi_worker.value,
  'naccu_kenya_admin',
  'naccu_naccu',
  'naccu_society_admin',
  'naccu_agent',
];

function getStoredUser(user) {
  if (user) return user;
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

function getUserRoleIds(user) {
  return (getStoredUser(user)?.user_role_assoc || []).map((role) => role.id);
}

export function isPtsiRoleUser(user) {
  const roles = getUserRoleIds(user);
  return roles.some((role) => ALL_PTSI_ROLES.includes(role));
}

export function setDocumentBranding(isPtsi) {
  document.title = isPtsi ? "ID Connect - Dashboard" : "Dimitra Dashboard";
  const href = isPtsi
    ? "https://dimitra-public-images.s3.us-east-1.amazonaws.com/ptsi_favicon.ico"
    : "https://dimitra-public-images.s3.amazonaws.com/dimitra_favicon.ico";

  // Match only actual favicon links (rel="icon"/"shortcut icon"), not apple-touch-icon etc.
  const iconLinks = document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']");
  if (iconLinks.length) {
    iconLinks.forEach((link) => {
      link.type = "image/x-icon";
      link.removeAttribute("sizes");
      link.href = href;
    });
  } else {
    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/x-icon";
    link.href = href;
    document.head.appendChild(link);
  }
}

export function getPtsiHomeRouteName(user) {
  return isPtsiRoleUser(user) ? 'dds_root_dashboard' : null;
}

export function isIndonesianClient(user) {
  const roles = getUserRoleIds(user);
  return roles.some((role) => ALL_INDONESIA_ROLES.includes(role));
}

/** Indonesia PTSI roles only — used for PTSI-specific EUDR proceed checks. */
export function isIndonesiaPtsiClient(user) {
  const roles = getUserRoleIds(user);
  return roles.some((role) => INDONESIA_PTSI_CLIENT_ROLES.includes(role));
}

export function isKenyaClient(user) {
  const roles = getUserRoleIds(user);
  return roles.some((role) => KENYA_CLIENT_ROLES.includes(role));
}

// CFA-121843: the buying-station register flow (owner/admin/clerk) is NACCU/Kenya-only.
export function canRegisterBuyingStation() {
  return isKenyaClient();
}

export function currentUser() {
  const user = JSON.parse(localStorage.getItem('user'));
  if(!user) return {};
  return user
}

export function isDdsExporter() {
  const user = JSON.parse(localStorage.getItem('user'));
  if(!user) return false;
  return user.user_role_assoc.some(role => role.id === 'dds_exporter');
}

/** Re-submit Payment / View Invoice are exporter-only. Hidden for cooperatives (indonesia_admin / supplier_owner). */
export function canSeeDdsPaymentMenu() {
  return isDdsExporter();
}

export function normalizeDdsStatus(status) {
  return String(status || '').trim().toLowerCase();
}

export function isFinalDdsComplianceStatus(status) {
  const normalized = normalizeDdsStatus(status);
  return normalized === 'compliant' || normalized === 'non-compliant';
}

export function isDdsCompliantStatus(status) {
  if (isIndonesianClient()) {
    return normalizeDdsStatus(status) === 'compliant';
  }
  return status === 'Compliant';
}

export function isDdsNonCompliantStatus(status) {
  if (isIndonesianClient()) {
    return normalizeDdsStatus(status) === 'non-compliant';
  }
  return status === 'Non-Compliant';
}

export function isWaitingForPaymentStatus(statusOrReport, statusLegends) {
  let legends = statusLegends;

  if (statusOrReport && typeof statusOrReport === 'object') {
    legends = statusOrReport.statusLegends;
  }

  return normalizeDdsStatus(legends) === 'waiting_for_payment';
}

export const DDS_PAYMENT_STATUS_FILTERS = {
  WAITING_FOR_PAYMENT: 'waiting_for_payment',
  PAYMENT_PENDING_VERIFICATION: 'payment_pending_verification',
  PAYMENT_NOT_RECEIVED: 'payment_not_received',
};

export function isDdsPaymentStatusFilter(value) {
  return Object.values(DDS_PAYMENT_STATUS_FILTERS).includes(value);
}

export function applyDdsListStatusFilter(requestParams, reportStatus) {
  if (!reportStatus) return requestParams;
  if (isDdsPaymentStatusFilter(reportStatus)) {
    requestParams.statusLegendsArray = [reportStatus];
  } else {
    requestParams.status = reportStatus;
  }
  return requestParams;
}

/** Receipt was sent back to the exporter — still waiting_for_payment, payment.status is rejected. */
export function isPaymentNotReceivedStatus(statusOrReport) {
  if (!statusOrReport || typeof statusOrReport !== 'object') return false;
  return isWaitingForPaymentStatus(statusOrReport)
    && normalizeDdsStatus(statusOrReport.payment?.status) === 'rejected';
}

export function isPaymentPendingVerificationStatus(statusOrReport, statusLegends) {
  let legends = statusLegends;

  if (statusOrReport && typeof statusOrReport === 'object') {
    legends = statusOrReport.statusLegends;
  }

  return normalizeDdsStatus(legends) === 'payment_pending_verification';
}

/** Payment legend takes priority over legacy workflow status badges (Pending, Under Review, etc.). */
export function hasActivePaymentStatusLegend(statusOrReport, statusLegends) {
  return isWaitingForPaymentStatus(statusOrReport, statusLegends)
    || isPaymentPendingVerificationStatus(statusOrReport, statusLegends);
}

export function isPendingApprovalStatus(statusOrReport, statusLegends) {
  let status = statusOrReport;
  let legends = statusLegends;

  if (statusOrReport && typeof statusOrReport === 'object') {
    status = statusOrReport.status;
    legends = statusOrReport.statusLegends;
  }

  if (isWaitingForPaymentStatus(statusOrReport, statusLegends)) {
    return false;
  }

  if (isPaymentPendingVerificationStatus(statusOrReport, statusLegends)) {
    return false;
  }

  if (normalizeDdsStatus(status) === 'pending approval') {
    return true;
  }

  // PTSI workflow: compliant/non-compliant reports stay pending until statusLegends is final.
  if (!isIndonesianClient()) {
    return false;
  }

  if (!isFinalDdsComplianceStatus(status)) {
    return false;
  }

  const normalizedLegends = normalizeDdsStatus(legends);
  if (
    normalizedLegends === 'approved'
    || normalizedLegends === 'temporary_approved'
    || normalizedLegends === 'rejected'
  ) {
    return false;
  }

  return true;
}

/** Official ID-Connect.io bank transfer destination for DDS review fees. */
export const DDS_PAYMENT_BANK_DETAILS = {
  purpose: 'ID-Connect.io Service Payment',
  accountName: 'PT Dimitra Teknologi Indonesia',
  accountNumber: '2040861877',
  bankName: 'PT Bank Central Asia Tbk. (BCA)',
  bankBranch: 'BCA KCP Bukit Cinere, Cinere, Depok 16514, West Java, Indonesia',
  swiftCode: 'CENAIDJA',
};

/** Temporarily hide bank transfer details on invoice / verify surfaces. PaymentModal step 3 always shows them. */
export function showDdsPaymentBankDetails() {
  return false;
}

/** Indonesia / Kenya PTSI panel reviewer (admin or worker role). */
export function isPtsiDdsReviewer(user) {
  return getUserRoleIds(user).some((role) => ALL_PTSI_ROLES.includes(role));
}

/** PTSI reviewers can approve DDS reports the same as indonesia_ptsi admin. */
export function canApproveDdsReport(user) {
  if (isPtsiDdsReviewer(user) && (isIndonesiaPtsiClient(user) || isKenyaClient(user))) {
    return true;
  }
  return checkPermission(PERMISSIONS.approve.value);
}

/** Indonesia PTSI reviewer can verify when payment is reported or legend + receipt indicate pending verification. */
export function canVerifyDdsPayment(item, { isPtsiReviewer = false, user } = {}) {
  const reviewer = isPtsiReviewer || isPtsiDdsReviewer(user);
  if (!reviewer || !isIndonesiaPtsiClient(user) || !item?.payment) return false;
  const paymentStatus = normalizeDdsStatus(item.payment.status);
  if (paymentStatus === 'reported') return true;
  return isPaymentPendingVerificationStatus(item) && !!item.payment.receipt_s3_key;
}

/** Resolve payer display fields from a DDS exporter row (internal share may have null exporter_email). */
export function resolveDdsExporterPayerFields(exporter, fallbackCompanyId) {
  if (!exporter) {
    return { name: '-', contact: '-', companyId: fallbackCompanyId || '-' };
  }

  const profile = exporter.exporter || {};
  const email = exporter.exporter_email || profile.email || profile.unverifiedEmail || null;
  const mobile = profile.mobile || profile.unverifiedMobile || null;
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim()
    || exporter.exporter_email
    || '-';
  const contact = email || mobile || '-';
  const companyId = profile.cf_userid || exporter.exporter_cf_id || fallbackCompanyId || '-';

  return { name, contact, companyId };
}

/** Exporter who accepted payment responsibility (or payer_cf_id match) when payment is locked. */
export function resolveDdsResponsibleExporter(exporters, payment) {
  const rows = Array.isArray(exporters)
    ? exporters
    : (exporters ? [exporters] : []);
  const activeRows = rows.filter((row) => row?.request_status !== 'rejected');
  if (!activeRows.length) return null;

  const acceptedExporter = activeRows.find(
    (row) => normalizeDdsStatus(row.payment_response_status) === 'accepted',
  );
  if (acceptedExporter) return acceptedExporter;

  const payerCfId = payment?.payer_cf_id;
  if (payerCfId) {
    const payer = activeRows.find(
      (row) => Number(row.exporter_cf_id) === Number(payerCfId),
    );
    if (payer) return payer;
  }

  return null;
}

/** When payment is locked, only the responsible exporter should appear in report detail. */
export function filterDdsExportersForDisplay(exporters, payment) {
  const responsibleExporter = resolveDdsResponsibleExporter(exporters, payment);
  if (responsibleExporter) return [responsibleExporter];

  const rows = Array.isArray(exporters)
    ? exporters
    : (exporters ? [exporters] : []);
  return rows.filter((row) => row?.request_status !== 'rejected');
}

/** True when another exporter accepted payment responsibility for this report. */
export function isDdsExporterLockedOutByPayment(item, currentUserCfId) {
  if (!item || !currentUserCfId) return false;

  const rows = item?.ddsReportExporters?.length
    ? item.ddsReportExporters
    : (item?.ddsReportExporter ? [item.ddsReportExporter] : []);
  const myEntry = rows.find(
    (row) => Number(row.exporter_cf_id) === Number(currentUserCfId),
  );
  if (!myEntry) return false;

  const acceptedByOther = rows.some(
    (row) => normalizeDdsStatus(row.payment_response_status) === 'accepted'
      && Number(row.exporter_cf_id) !== Number(currentUserCfId),
  );
  return acceptedByOther;
}

/** Hide DDS row actions for exporters locked out after another exporter accepted payment. */
export function shouldHideDdsExporterRowActions(item, currentUserCfId) {
  return isDdsExporterLockedOutByPayment(item, currentUserCfId);
}

export function isPtsiApproval(){
  const user = JSON.parse(localStorage.getItem('user'));
  if(!user) return false;
  return user.user_role_assoc.some(role => PTSI_ADMIN_ROLES.includes(role.id));
}

export const EventBus = new Vue();

/**
 * Decode HTML entities in a string
 * @param {string} str - The string containing HTML entities
 * @returns {string} - The decoded string
 */
export function decodeHtmlEntities(str) {
  if (!str || typeof str !== 'string') {
    return str;
  }
  
  // Decode HTML entities, handling double/multi-encoded values
  // (e.g. "&amp;#x2F;" -> "&#x2F;" -> "/") by decoding repeatedly
  // until the string stops changing.
  const textarea = document.createElement('textarea');
  let current = str;
  for (let i = 0; i < 5; i++) {
    textarea.innerHTML = current;
    const decoded = textarea.value;
    if (decoded === current) break;
    current = decoded;
  }
  return current.trim();
}

/**
 * Check if user has permission for a specific module and action
 * @param {string} moduleId - The module ID to check (e.g., 'indonesia_ptsi_admin_roles')
 * @returns {boolean} - True if user has permission, false otherwise
 */
export function checkPermission(moduleId) {
  try {
    const moduleAndPermissions = JSON.parse(localStorage.getItem('moduleAndPermissions') || '[]');
    const rolesIds = JSON.parse(localStorage.getItem('user') || '[]')?.user_role_assoc?.map(role => role.id);
    if (!moduleAndPermissions || !Array.isArray(moduleAndPermissions) || !rolesIds.length) {
      return false;
    }
    
    // Create eligible permission IDs by combining roles with permissionId (mirrors backend logic)
    const eligiblePermissionId = rolesIds.map((role) => `${role}_${moduleId}`); 
    // Check if user has permission for this module (mirrors backend hasPermission logic)
    return moduleAndPermissions.some((item) => {
      const hasPermission = eligiblePermissionId.includes(item.module_id);
      return hasPermission && (item.permitted === 1 || item.permitted === true);
    }) || false;

  } catch (error) {
    console.error('Permission check failed:', error);
    return false;
  }
}

